import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Camera, Check, Loader2, RefreshCw, X, ShieldCheck, AlertTriangle, CheckCircle } from 'lucide-react';

import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// A stalled upload on weak mobile data used to leave the screen on "Verifying face..."
// forever. Every face API call now fails loudly after 45s instead of hanging.
async function postWithTimeout(url, body, ms = 60000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, {
      method: 'POST', headers: h(), credentials: 'include',
      body: JSON.stringify(body), signal: ctrl.signal,
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      const err = new Error('The server did not respond in time. Check your internet and try again.');
      err.timedOut = true;
      throw err;
    }
    const err = new Error('Network error — please check your internet connection and try again.');
    err.networkError = true;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}


const MODELS_URL = '/models';

// ============ MODEL LOADER ============
let modelsLoaded = false;
let activeBackend = '';

export function getFaceBackend() {
  return activeBackend;
}

// Some phones (older Android Chrome, iOS Safari in Low Power / Lockdown mode, or devices
// where WebGL is blacklisted) leave TensorFlow.js without an initialised backend, which made
// model loading fail with "Backend 'undefined' has not yet been initialized" — the user then
// saw only a black box with the face circle and nothing ever happened. Pick a backend
// explicitly, falling back all the way to CPU, before loading any model.
async function initTfBackend() {
  const tf = faceapi.tf;
  if (!tf) return '';
  const tryBackend = async (name) => {
    try {
      await tf.setBackend(name);
      await tf.ready();
      return tf.getBackend() === name;
    } catch {
      return false;
    }
  };
  // WebGL first; straight to CPU if it is unavailable. (The wasm tier is skipped on purpose:
  // the wasm binaries are not hosted with this build, so trying it only wasted seconds
  // and logged hard errors on low-end phones.)
  if (!(await tryBackend('webgl'))) {
    await tryBackend('cpu');
  }
  try { await tf.ready(); } catch {}
  return tf.getBackend() || '';
}

async function loadModels() {
  if (modelsLoaded) return activeBackend;
  activeBackend = await initTfBackend();
  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL),
    faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URL),
  ]);
  modelsLoaded = true;
  return activeBackend;
}

// Some mobile GPUs (certain Mali / Adreno drivers) give a working WebGL context that returns
// garbage from float-texture readback: the scan then runs forever and detects nothing, even
// with a perfectly lit face. Falling back to the CPU backend is slower but always correct.
let cpuFallbackDone = false;
async function switchToCpuBackend() {
  const tf = faceapi.tf;
  if (!tf || cpuFallbackDone || activeBackend === 'cpu') return false;
  cpuFallbackDone = true;
  try {
    faceapi.nets.tinyFaceDetector.dispose();
    faceapi.nets.faceLandmark68Net.dispose();
    faceapi.nets.faceRecognitionNet.dispose();
  } catch { /* not loaded yet */ }
  try {
    await tf.setBackend('cpu');
    await tf.ready();
  } catch {
    return false;
  }
  activeBackend = tf.getBackend() || 'cpu';
  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL),
    faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URL),
  ]);
  return true;
}

// Detection runs on a SMALL, pre-scaled copy of the frame instead of the raw <video>.
// Feeding the full camera frame made every pass allocate + resize a large tensor on the
// main thread, which froze the preview (and the whole page) on mid-range phones.
// One canvas is reused for the entire session — no per-frame allocations.
let _workCanvas = null;
function frameFor(video, { zoom = false, maxW = 320 } = {}) {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return video;
  const scale = Math.min(1, maxW / vw);
  const w = Math.round(vw * scale), h = Math.round(vh * scale);
  if (!_workCanvas) _workCanvas = document.createElement('canvas');
  const c = _workCanvas;
  if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
  const ctx = c.getContext('2d', { alpha: false });
  if (zoom) {
    // A face held very close fills the whole frame, which the tiny detector can miss.
    // Letterboxing it restores a normal face-to-frame ratio.
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, w, h);
    const iw = w * 0.6, ih = h * 0.6;
    ctx.drawImage(video, (w - iw) / 2, (h - ih) / 2, iw, ih);
  } else {
    ctx.drawImage(video, 0, 0, w, h);
  }
  return c;
}

// Guided multi-angle enrolment — three descriptors make matching far more forgiving
// (haircut / glasses / lighting / head tilt) than a single front-on capture.
const REGISTER_ANGLES = [
  { key: 'front', label: 'Look straight at the camera', short: 'Front' },
  { key: 'left', label: 'Turn your head slightly to your LEFT', short: 'Left' },
  { key: 'right', label: 'Turn your head slightly to your RIGHT', short: 'Right' },
];

// ============ FACE CAPTURE COMPONENT ============
export function FaceCapture({ onCapture, onCancel, mode = 'register', loading: externalLoading }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);
  const detectStartRef = useRef(Date.now());
  const consecutiveErrorsRef = useRef(0);
  const noFaceTicksRef = useRef(0);
  // Self-healing scan loop refs — the loop must NEVER die, otherwise the user is left
  // staring at a live camera with a disabled button (the exact bug staff reported).
  const runningRef = useRef(false);
  const timerRef = useRef(null);
  const tickRef = useRef(null);
  const framesRef = useRef(0);
  const detectionsRef = useRef(0);
  const lastErrRef = useRef('');
  const lastDetectMsRef = useRef(0);
  const sensitiveRef = useRef(false);
  const zoomOutRef = useRef(false);
  const capturingRef = useRef(false);
  const autoTriggeredRef = useRef(false);
  const [canCapture, setCanCapture] = useState(false);
  const [modelError, setModelError] = useState('');
  const [diag, setDiag] = useState('');
  const [modelsReady, setModelsReady] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [status, setStatus] = useState('Loading face detection models...');
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [tooSlow, setTooSlow] = useState(false); // shown after 20s no face / repeated errors
  const [hint, setHint] = useState('');
  const faceStableCountRef = useRef(0);
  // Multi-angle enrolment (register mode only)
  const multiAngle = mode === 'register';
  const capturedRef = useRef([]);           // [{descriptor, snapshot}]
  const [angleIdx, setAngleIdx] = useState(0);
  // Frozen-preview recovery — some phones silently stall the video track
  const lastVideoTimeRef = useRef(-1);
  const staleTicksRef = useRef(0);
  const [frozen, setFrozen] = useState(false);

  useEffect(() => {
    startFlow();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startFlow = () => {
    setModelError('');
    setStatus('Loading face detection models...');
    loadModels().then((backend) => {
      setModelsReady(true);
      setStatus(`Models loaded (${backend || 'default'}). Starting camera...`);
      startCamera();
    }).catch((e) => {
      lastErrRef.current = e?.message || 'model load failed';
      setModelError(lastErrRef.current);
      setStatus('Could not load the face scanner.');
    });
  };

  // 20-second watchdog: if no face is captured by then, show the "having trouble?" panel.
  useEffect(() => {
    const t = setInterval(() => {
      if (autoTriggered || capturing) return;
      const elapsed = Date.now() - detectStartRef.current;
      if (cameraActive && elapsed > 20000 && !faceDetected) {
        setTooSlow(true);
        setHint('We could not detect your face for 20 seconds.');
      }
    }, 1000);
    return () => clearInterval(t);
  }, [cameraActive, faceDetected, autoTriggered, capturing]);

  const startCamera = async () => {
    try {
      // Mobile-friendly camera constraints — smaller resolution = much faster CPU/GPU pass
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 480 }, height: { ideal: 360 }, facingMode: 'user' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* iOS may reject the first play() — the loop retries */ }
        setCameraActive(true);
        setStatus('Position your face in the frame');
        detectStartRef.current = Date.now();
        consecutiveErrorsRef.current = 0;
        noFaceTicksRef.current = 0;
        framesRef.current = 0;
        detectionsRef.current = 0;
        sensitiveRef.current = false;
        lastVideoTimeRef.current = -1;
        staleTicksRef.current = 0;
        setFrozen(false);
        // Manual capture becomes available shortly after the camera starts, so nobody
        // is ever blocked by a face outline that refuses to turn green.
        setTimeout(() => setCanCapture(true), 3000);
        runningRef.current = true;
        schedule(0);
      }
    } catch (err) {
      const denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
      const inUse = err && err.name === 'NotReadableError';
      setStatus(denied
        ? 'Camera access denied. Please allow camera permission in your browser, then reload the page.'
        : inUse
          ? 'Camera is being used by another app. Please close it and retry.'
          : 'Could not start camera. Please reload the page and retry.');
    }
  };

  const stopCamera = () => {
    runningRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  };

  // A stalled camera track cannot be revived by play() alone — the stream has to be
  // torn down and re-acquired. This is what fixes the "preview freezes and the outline
  // never turns green" report on phones.
  const restartCamera = async () => {
    setFrozen(false);
    setStatus('Restarting the camera...');
    runningRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (streamRef.current) { try { streamRef.current.getTracks().forEach(t => t.stop()); } catch {} }
    streamRef.current = null;
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch {} }
    setCameraActive(false);
    await new Promise(r => setTimeout(r, 300));
    await startCamera();
  };

  // Always reschedules while the camera is running — no code path may leave the loop dead.
  const schedule = (delay = 200) => {
    if (!runningRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!runningRef.current) return;
      if (tickRef.current) tickRef.current();
    }, delay);
  };

  const tick = async () => {
    if (!runningRef.current) return;
    const v = videoRef.current;
    try {
      if (!v) { schedule(300); return; }
      // Mobile browsers (iOS Safari / Android Chrome) frequently leave the element paused
      // after the first play(), or pause it when the app is backgrounded. Recover instead of dying.
      if (v.paused || v.ended) {
        try { await v.play(); } catch { /* retry on the next tick */ }
      }
      if (!v.videoWidth || v.readyState < 2) {
        setStatus('Waiting for the camera to start...');
        schedule(300);
        return;
      }
      framesRef.current += 1;
      if (framesRef.current === 1) setStatus('Position your face in the frame');

      // Frozen-track watchdog: if currentTime stops advancing the preview is dead.
      if (v.currentTime === lastVideoTimeRef.current) {
        staleTicksRef.current += 1;
        if (staleTicksRef.current === 6) { try { await v.play(); } catch {} }
        if (staleTicksRef.current >= 15 && !capturingRef.current) {
          setFrozen(true);
          staleTicksRef.current = 0;
          await restartCamera();
          return;
        }
      } else {
        lastVideoTimeRef.current = v.currentTime;
        staleTicksRef.current = 0;
        if (frozen) setFrozen(false);
      }

      const t0 = Date.now();
      const opts = sensitiveRef.current
        ? new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.25 })
        : new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 });
      const input = frameFor(v, { zoom: zoomOutRef.current, maxW: sensitiveRef.current ? 360 : 320 });
      const detection = await faceapi.detectSingleFace(input, opts).withFaceLandmarks();
      lastDetectMsRef.current = Date.now() - t0;
      consecutiveErrorsRef.current = 0;

      if (canvasRef.current && !zoomOutRef.current) {
        const ds = { width: v.videoWidth, height: v.videoHeight };
        faceapi.matchDimensions(canvasRef.current, ds);
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, ds.width, ds.height);
        if (detection) {
          const resized = faceapi.resizeResults(detection, ds);
          faceapi.draw.drawDetections(canvasRef.current, resized);
          faceapi.draw.drawFaceLandmarks(canvasRef.current, resized);
        }
      } else if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }

      if (detection) {
        detectionsRef.current += 1;
        setFaceDetected(true);
        setCanCapture(true);
        noFaceTicksRef.current = 0;
        if (mode === 'checkin' && !autoTriggeredRef.current) {
          faceStableCountRef.current += 1;
          if (faceStableCountRef.current >= 6) {
            autoTriggeredRef.current = true;
            setAutoTriggered(true);
            handleCapture();
            return;   // handleCapture stops the camera on success
          }
        }
      } else {
        setFaceDetected(false);
        faceStableCountRef.current = 0;
        noFaceTicksRef.current += 1;
        // ~3s without a face: switch to the slower, far more sensitive scan
        if (noFaceTicksRef.current === 15) sensitiveRef.current = true;
        // ~5s: the face may be filling the whole frame — scan a zoomed-out copy
        if (noFaceTicksRef.current === 25) {
          zoomOutRef.current = true;
          setStatus('Adjusting the scan for close-up faces...');
        }
        // ~8s and still nothing while frames ARE being processed: the GPU path is likely
        // returning garbage on this device. Reload the models on the CPU backend.
        if (noFaceTicksRef.current === 40 && !cpuFallbackDone && activeBackend !== 'cpu') {
          setStatus('Switching to compatibility mode for this device...');
          runningRef.current = false;
          const ok = await switchToCpuBackend();
          runningRef.current = true;
          if (ok) {
            noFaceTicksRef.current = 0;
            zoomOutRef.current = false;
            sensitiveRef.current = true;
            setStatus('Compatibility mode active. Position your face in the frame');
            toast.info('Switched the face scanner to compatibility mode for your device.');
            schedule(0);
            return;
          }
        }
        if (noFaceTicksRef.current === 55) {
          setHint('Tip: hold the phone about an arm\u2019s length away, face a bright light and remove sunglasses/mask. You can also tap the button below to capture anyway.');
        }
      }
    } catch (e) {
      lastErrRef.current = e?.message || String(e);
      consecutiveErrorsRef.current += 1;
      sensitiveRef.current = true;
      if (consecutiveErrorsRef.current === 15) {
        setTooSlow(true);
        setHint(`Face scanner error: ${lastErrRef.current}`);
      }
    }
    if (framesRef.current % 5 === 0) {
      setDiag(`${activeBackend || 'default'} · camera ${v?.videoWidth || 0}x${v?.videoHeight || 0} · scanned ${framesRef.current} · faces ${detectionsRef.current} · ${lastDetectMsRef.current}ms`
        + (sensitiveRef.current ? ' · high-sensitivity' : '')
        + (zoomOutRef.current ? ' · zoom-out' : '')
        + (lastErrRef.current ? ` · last error: ${lastErrRef.current}` : ''));
    }
    // Give the browser at least as long as the scan took to repaint the preview —
    // hogging the main thread back-to-back is what made the camera look frozen.
    schedule(Math.min(900, Math.max(180, lastDetectMsRef.current)));
  };

  useEffect(() => { tickRef.current = tick; });

  const copyDiag = async () => {
    const text = `${diag || 'no scan data'} | ua: ${navigator.userAgent}`;
    try { await navigator.clipboard.writeText(text); toast.success('Diagnostics copied — please send it to HR'); }
    catch { toast.info(text); }
  };

  const handleCapture = async () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);
    setStatus('Verifying face...');
    // Watchdog: never leave the user staring at "Processing..." forever
    const watchdog = setTimeout(() => {
      if (capturingRef.current) {
        setCapturing(false);
        capturingRef.current = false;
        setTooSlow(true);
        setHint('That took too long on this device. Move to brighter light, hold the phone an arm\u2019s length away and tap the button again.');
        toast.error('Face check timed out on this device. Please try again.');
      }
    }, 40000);
    capturingRef.current = true;
    try {
      const v = videoRef.current;
      if (v.paused) { try { await v.play(); } catch {} }
      // Progressive passes. On a slow (CPU) backend the big 512px pass can take tens of
      // seconds on a phone, so the pass list is trimmed and relies on the zoom-out trick
      // instead of brute-force resolution.
      const slow = activeBackend !== 'webgl';
      const passes = slow
        ? [{ inputSize: 320, scoreThreshold: 0.35, zoom: false, maxW: 360 },
           { inputSize: 320, scoreThreshold: 0.2, zoom: true, maxW: 360 }]
        : [{ inputSize: 320, scoreThreshold: 0.4, zoom: false, maxW: 420 },
           { inputSize: 416, scoreThreshold: 0.25, zoom: false, maxW: 480 },
           { inputSize: 416, scoreThreshold: 0.25, zoom: true, maxW: 480 },
           { inputSize: 512, scoreThreshold: 0.15, zoom: true, maxW: 560 }];
      const runPasses = async () => {
        for (let i = 0; i < passes.length; i++) {
          const p = passes[i];
          setStatus(`Verifying face... (check ${i + 1} of ${passes.length})`);
          const input = frameFor(v, { zoom: p.zoom, maxW: p.maxW });
          // eslint-disable-next-line no-await-in-loop
          const d = await faceapi
            .detectSingleFace(input, new faceapi.TinyFaceDetectorOptions({ inputSize: p.inputSize, scoreThreshold: p.scoreThreshold }))
            .withFaceLandmarks()
            .withFaceDescriptor();
          if (d) return d;
        }
        return null;
      };
      let detection = await runPasses();
      if (!detection && !cpuFallbackDone && activeBackend !== 'cpu') {
        // Last resort: this device's GPU path is not producing usable results.
        setStatus('Switching to compatibility mode...');
        if (await switchToCpuBackend()) detection = await runPasses();
      }

      if (!detection) {
        clearTimeout(watchdog);
        toast.error('Could not read your face. Move closer, turn on more light, then tap the button again.');
        sensitiveRef.current = true;
        setCapturing(false);
        capturingRef.current = false;
        setAutoTriggered(false);
        autoTriggeredRef.current = false;
        faceStableCountRef.current = 0;
        return;
      }
      const descriptor = Array.from(detection.descriptor);

      // Keep the snapshot small — a full-resolution phone JPEG can be a megabyte and the
      // upload then stalls (or is rejected) on mobile data, which looked like "stuck on
      // Verifying face".
      let snapshot = null;
      try {
        const maxW = 420;
        const scale = Math.min(1, maxW / (v.videoWidth || maxW));
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = Math.round((v.videoWidth || maxW) * scale);
        tempCanvas.height = Math.round((v.videoHeight || maxW) * scale);
        tempCanvas.getContext('2d').drawImage(v, 0, 0, tempCanvas.width, tempCanvas.height);
        snapshot = tempCanvas.toDataURL('image/jpeg', 0.55);
      } catch {}

      clearTimeout(watchdog);
      if (multiAngle) {
        capturedRef.current = [...capturedRef.current, { descriptor, snapshot }];
        const done = capturedRef.current.length;
        if (done < REGISTER_ANGLES.length) {
          setAngleIdx(done);
          setStatus(REGISTER_ANGLES[done].label);
          toast.success(`Captured ${done} of ${REGISTER_ANGLES.length}. Now: ${REGISTER_ANGLES[done].label}`);
          capturingRef.current = false;
          setCapturing(false);
          return;
        }
        setStatus('Submitting...');
        stopCamera();
        onCapture(capturedRef.current.map(c => c.descriptor), capturedRef.current.map(c => c.snapshot));
      } else {
        setStatus('Submitting...');
        stopCamera();
        onCapture(descriptor, snapshot);
      }
    } catch (e) {
      clearTimeout(watchdog);
      lastErrRef.current = e?.message || 'unknown error';
      toast.error('Face capture failed: ' + lastErrRef.current + '. Please try again.');
      setAutoTriggered(false);
      autoTriggeredRef.current = false;
      faceStableCountRef.current = 0;
    }
    capturingRef.current = false;
    setCapturing(false);
  };

  const handleRetry = () => {
    setTooSlow(false);
    setHint('');
    detectStartRef.current = Date.now();
    noFaceTicksRef.current = 0;
    faceStableCountRef.current = 0;
    consecutiveErrorsRef.current = 0;
    sensitiveRef.current = true;
    setAutoTriggered(false);
    autoTriggeredRef.current = false;
    if (!runningRef.current) { runningRef.current = true; }
    schedule(0);
  };

  return (
    <div className="space-y-3" data-testid="face-capture">
      {/* Multi-angle enrolment guidance */}
      {multiAngle && (
        <div className="max-w-md mx-auto" data-testid="face-angle-guide">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm font-semibold text-slate-800">
              Step {Math.min(angleIdx + 1, REGISTER_ANGLES.length)} of {REGISTER_ANGLES.length} — {REGISTER_ANGLES[angleIdx]?.label}
            </p>
            <span className="text-xs text-slate-500">{capturedRef.current.length}/{REGISTER_ANGLES.length} captured</span>
          </div>
          <div className="flex gap-1.5">
            {REGISTER_ANGLES.map((a, i) => (
              <div key={a.key} className="flex-1" data-testid={`face-angle-dot-${a.key}`}>
                <div className={`h-1.5 rounded-full ${i < capturedRef.current.length ? 'bg-green-500' : i === angleIdx ? 'bg-[#DC2626]' : 'bg-slate-200'}`} />
                <p className={`text-[10px] mt-1 text-center ${i === angleIdx ? 'text-[#DC2626] font-semibold' : 'text-slate-400'}`}>{a.short}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Camera View */}
      <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3] max-w-md mx-auto">
        <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted playsInline />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

        {/* Overlay: face guide */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`w-44 h-56 border-2 rounded-[50%] transition-colors duration-300 ${faceDetected ? 'border-green-400' : 'border-white/40'}`} />
        </div>

        {/* Auto-detect indicator */}
        {mode === 'checkin' && faceDetected && !autoTriggered && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full animate-pulse">
            Hold still... verifying
          </div>
        )}

        {capturing && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">Verifying face...</p>
            </div>
          </div>
        )}

        {!modelsReady && !modelError && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">{status}</p>
            </div>
          </div>
        )}

        {/* Model / scanner load failure — explicit, actionable, never an endless spinner */}
        {modelError && (
          <div className="absolute inset-0 bg-black/90 flex items-center justify-center px-4" data-testid="face-model-error">
            <div className="text-center text-white space-y-3 max-w-xs">
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" />
              <p className="text-sm font-semibold">Face scanner could not start on this device</p>
              <p className="text-[11px] text-slate-300 break-words">{modelError}</p>
              <p className="text-[11px] text-slate-300">
                Check your internet, then tap Try Again. If it keeps failing, use Chrome (Android) or Safari (iPhone)
                with normal power mode, or ask HR to mark your attendance manually.
              </p>
              <div className="flex gap-2 justify-center">
                <Button size="sm" variant="outline" className="text-slate-900" onClick={startFlow} data-testid="face-model-retry">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Try Again
                </Button>
                {onCancel && (
                  <Button size="sm" variant="outline" className="text-slate-900" onClick={onCancel} data-testid="face-model-cancel">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {modelsReady && !cameraActive && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center px-4">
            <div className="text-center text-white space-y-3 max-w-xs">
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" />
              <p className="text-sm">{status}</p>
              <Button size="sm" variant="outline" className="text-slate-900" onClick={() => window.location.reload()} data-testid="camera-reload-btn">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Reload Page
              </Button>
            </div>
          </div>
        )}

        {/* Frozen preview recovery */}
        {frozen && (
          <div className="absolute inset-0 bg-black/85 flex items-center justify-center px-4" data-testid="face-frozen-overlay">
            <div className="text-center text-white space-y-3 max-w-xs">
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" />
              <p className="text-sm font-semibold">Camera preview stopped responding</p>
              <p className="text-[11px] text-slate-300">Restarting it now. If the picture stays frozen, tap Restart Camera.</p>
              <Button size="sm" variant="outline" className="text-slate-900" onClick={restartCamera} data-testid="face-frozen-restart">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Restart Camera
              </Button>
            </div>
          </div>
        )}

        {/* "Having trouble?" overlay — appears after 20s of no face / repeated errors */}
        {tooSlow && cameraActive && (
          <div className="absolute inset-0 bg-black/85 flex items-center justify-center px-4 backdrop-blur-sm" data-testid="face-trouble-overlay">
            <div className="text-center text-white space-y-3 max-w-sm">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" />
              <p className="text-base font-semibold">Having trouble detecting your face?</p>
              <p className="text-xs text-slate-200 leading-relaxed">{hint || 'Try these quick fixes:'}</p>
              <ul className="text-xs text-slate-200 text-left list-disc pl-6 space-y-1 max-w-xs mx-auto">
                <li>Move to a brighter spot — avoid backlight</li>
                <li>Bring your face closer (fill the oval guide)</li>
                <li>Remove sunglasses or face mask</li>
                <li>Switch to your phone if you're on laptop with weak camera</li>
              </ul>
              <div className="flex gap-2 justify-center pt-2 flex-wrap">
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100" onClick={handleRetry} data-testid="face-trouble-retry">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Try Again
                </Button>
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100" onClick={restartCamera} data-testid="face-trouble-restart-camera">
                  Restart Camera
                </Button>
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100" onClick={() => window.location.reload()} data-testid="face-trouble-reload">
                  Reload Page
                </Button>
                {onCancel && (
                  <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100" onClick={onCancel} data-testid="face-trouble-cancel">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Inline hint (sub-text under camera) when not yet in trouble overlay */}
      {hint && !tooSlow && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 text-center" data-testid="face-inline-hint">
          {hint}
        </div>
      )}

      {/* Status indicators */}
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Badge variant={faceDetected ? 'default' : 'secondary'} className={faceDetected ? 'bg-green-100 text-green-800' : ''}>
          {faceDetected ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
          Face {faceDetected ? 'Detected' : 'Not Found'}
        </Badge>
        {mode === 'checkin' && faceDetected && (
          <Badge className="bg-blue-100 text-blue-700 animate-pulse">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Auto-verifying...
          </Badge>
        )}
      </div>

      {/* Actions — a manual capture is ALWAYS reachable once the camera is up, so a face
          outline that never turns green can no longer block registration or attendance. */}
      <div className="flex gap-2 justify-center flex-wrap">
        {onCancel && (
          <Button variant="outline" onClick={() => { stopCamera(); onCancel(); }} data-testid="face-cancel-btn">
            Cancel
          </Button>
        )}
        <Button
          onClick={handleCapture}
          disabled={(!faceDetected && !canCapture) || capturing || externalLoading}
          className="bg-[#DC2626] hover:bg-[#B91C1C] text-white min-w-[180px]"
          data-testid={mode === 'register' ? 'face-capture-btn' : 'face-manual-checkin-btn'}
        >
          {capturing || externalLoading ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
          ) : mode === 'register' ? (
            <><Camera className="w-4 h-4 mr-2" />
              {capturedRef.current.length >= REGISTER_ANGLES.length - 1
                ? 'Capture & Register'
                : `Capture ${REGISTER_ANGLES[angleIdx]?.short || ''} (${capturedRef.current.length + 1}/${REGISTER_ANGLES.length})`}
            </>
          ) : (
            <><Camera className="w-4 h-4 mr-2" /> {faceDetected ? 'Mark Attendance Now' : 'Try Anyway'}</>
          )}
        </Button>
        {cameraActive && (
          <Button variant="ghost" size="sm" onClick={restartCamera} className="text-slate-500" data-testid="face-restart-camera-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Restart camera
          </Button>
        )}
      </div>

      {!faceDetected && canCapture && !capturing && (
        <p className="text-[11px] text-center text-slate-500" data-testid="face-capture-anyway-hint">
          Outline not turning green? Tap the button — we will do a slower, more sensitive scan.
        </p>
      )}

      {/* Diagnostics — lets a stuck user send HR the exact reason */}
      {cameraActive && (
        <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400" data-testid="face-diagnostics">
          <span className="truncate max-w-[70%]">{diag || 'starting scan...'}</span>
          <button type="button" onClick={copyDiag} className="underline hover:text-slate-600" data-testid="face-diag-copy">
            Copy details
          </button>
        </div>
      )}
    </div>
  );
}

// ============ FACE REGISTRATION PANEL ============
export function FaceRegistrationPanel({ employeeId, isHR = false, onRegistered }) {
  const [faceStatus, setFaceStatus] = useState(null);
  const [showCapture, setShowCapture] = useState(false);
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (employeeId) fetchStatus();
  }, [employeeId]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/face/status/${employeeId}`, { headers: h(), credentials: 'include' });
      if (res.ok) setFaceStatus(await res.json());
    } catch {}
  };

  const handleRegister = async (descriptor, snapshot) => {
    // Hard guard against double-submission from the same client (rapid clicks / multiple captures)
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      // Multi-angle enrolment sends an array of descriptors (front / left / right).
      const isMulti = Array.isArray(descriptor);
      const body = isMulti
        ? { employee_id: employeeId, descriptors: descriptor, face_snapshots: snapshot, face_snapshot: (snapshot || [])[0] }
        : { employee_id: employeeId, descriptor, face_snapshot: snapshot };
      const res = await postWithTimeout(`${API_URL}/api/face/register`, body);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Face registration failed. Please try again.');
      toast.success(data.message || 'Face registration submitted. Please wait for HR approval.');
      setShowCapture(false);
      fetchStatus();
    } catch (e) {
      toast.error(e.message);
      // If a duplicate pending request was rejected by backend, refresh status
      // so the UI immediately reflects the pending state.
      if (e.message && e.message.toLowerCase().includes('already submitted')) {
        setShowCapture(false);
        fetchStatus();
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove face registration? Employee will need to re-register.')) return;
    try {
      const res = await fetch(`${API_URL}/api/face/registration/${employeeId}`, {
        method: 'DELETE', headers: h(), credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Face registration removed');
      fetchStatus();
    } catch (e) { toast.error(e.message); }
  };

  if (!employeeId) return null;

  const isPending = !!faceStatus?.pending;
  const isApproved = !!faceStatus?.registered;

  return (
    <Card data-testid="face-registration-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#DC2626]" />
          Face Registration
        </CardTitle>
      </CardHeader>
      <CardContent>
        {showCapture ? (
          <FaceCapture onCapture={handleRegister} onCancel={() => setShowCapture(false)} mode="register" loading={loading} />
        ) : (
          <div className="space-y-3">
            {isApproved ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Face Registered</p>
                    <p className="text-xs text-green-600">
                      Registered on {faceStatus.registration?.created_at ? new Date(faceStatus.registration.created_at).toLocaleDateString('en-IN') : 'N/A'}
                      {faceStatus.registration?.angles_captured > 1 ? ` · ${faceStatus.registration.angles_captured} angles` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowCapture(true)} data-testid="face-reregister-btn">
                    <RefreshCw className="w-3 h-3 mr-1" /> Re-register
                  </Button>
                  {isHR && (
                    <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="face-delete-btn">
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            ) : isPending ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="face-pending-state">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Request Submitted</p>
                    <p className="text-xs text-blue-600">
                      Awaiting HR approval{faceStatus.pending_request?.created_at ? ` since ${new Date(faceStatus.pending_request.created_at).toLocaleDateString('en-IN')}` : ''}. You will be notified once approved.
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled data-testid="face-pending-btn">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Pending
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Face Not Registered</p>
                    <p className="text-xs text-amber-600">Required for face-verified attendance</p>
                  </div>
                </div>
                <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={() => setShowCapture(true)} data-testid="face-register-btn">
                  <Camera className="w-3 h-3 mr-1" /> Register Face
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ ATTENDANCE READINESS PANEL ============
// Live preflight checks: internet quality, GPS, in-range status.
function AttendanceReadiness({ location }) {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [conn, setConn] = useState(null); // { effectiveType, downlink }
  const [permState, setPermState] = useState('unknown'); // granted / prompt / denied / unknown

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Network Information API (Chrome / Android)
    const nav = navigator;
    const connObj = nav.connection || nav.mozConnection || nav.webkitConnection;
    if (connObj) {
      const update = () => setConn({ effectiveType: connObj.effectiveType, downlink: connObj.downlink });
      update();
      connObj.addEventListener && connObj.addEventListener('change', update);
    }

    // Geolocation permission
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then(p => {
        setPermState(p.state);
        p.onchange = () => setPermState(p.state);
      }).catch(() => setPermState('unknown'));
    }

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Net quality bucket
  let netLabel = 'Good';
  let netColor = 'green';
  if (!online) { netLabel = 'Offline'; netColor = 'red'; }
  else if (conn) {
    const et = conn.effectiveType || '';
    if (et === 'slow-2g' || et === '2g') { netLabel = 'Slow (2G)'; netColor = 'red'; }
    else if (et === '3g') { netLabel = 'Fair (3G)'; netColor = 'amber'; }
    else if (et === '4g') {
      // downlink in Mbps if available
      if (conn.downlink && conn.downlink < 1.5) { netLabel = 'Fair'; netColor = 'amber'; }
      else { netLabel = 'Good (4G)'; netColor = 'green'; }
    }
  }

  // GPS state
  let gpsLabel = 'Waiting for GPS…';
  let gpsColor = 'amber';
  if (location && location.accuracy != null) {
    const acc = Math.round(location.accuracy);
    if (acc <= 50) { gpsLabel = `Active (${acc}m)`; gpsColor = 'green'; }
    else if (acc <= 200) { gpsLabel = `Active (${acc}m)`; gpsColor = 'amber'; }
    else { gpsLabel = `Weak (${acc}m)`; gpsColor = 'red'; }
  } else if (location) {
    gpsLabel = 'Active'; gpsColor = 'green';
  } else if (permState === 'denied') {
    gpsLabel = 'GPS Blocked'; gpsColor = 'red';
  }

  // In-range — we don't know definitively until backend checks; we surface "Will verify on submit"
  const rangeLabel = location ? 'Will verify on submit' : 'Awaiting GPS';

  const colorClass = (c) => c === 'green' ? 'bg-green-100 text-green-700 border-green-200'
                          : c === 'red'   ? 'bg-red-100 text-red-700 border-red-200'
                          : 'bg-amber-100 text-amber-700 border-amber-200';

  return (
    <div className="grid grid-cols-3 gap-2 mb-3" data-testid="attendance-readiness">
      <div className={`text-center p-2 rounded-lg border text-xs ${colorClass(netColor)}`} data-testid="readiness-net">
        <p className="font-semibold leading-tight">📶 Internet</p>
        <p className="text-[10px] mt-0.5 leading-tight">{netLabel}</p>
      </div>
      <div className={`text-center p-2 rounded-lg border text-xs ${colorClass(gpsColor)}`} data-testid="readiness-gps">
        <p className="font-semibold leading-tight">📍 Location</p>
        <p className="text-[10px] mt-0.5 leading-tight">{gpsLabel}</p>
      </div>
      <div className={`text-center p-2 rounded-lg border text-xs ${colorClass(location ? 'amber' : 'amber')}`} data-testid="readiness-range">
        <p className="font-semibold leading-tight">🎯 In Range</p>
        <p className="text-[10px] mt-0.5 leading-tight">{rangeLabel}</p>
      </div>
    </div>
  );
}

// ============ FACE+GEO CHECK-IN COMPONENT ============
export function FaceGeoCheckIn({ onSuccess, todayRecord }) {
  const [step, setStep] = useState('select'); // select -> geo -> face -> done
  const [action, setAction] = useState(null); // 'check_in' or 'check_out'
  const [location, setLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState('Getting your location...');
  const [geoError, setGeoError] = useState(null); // { code, message }
  const [loading, setLoading] = useState(false);
  const [doneInfo, setDoneInfo] = useState(null); // {action, time, date, work_hours}
  const [faceMismatchCount, setFaceMismatchCount] = useState(0);
  const [faceExempt, setFaceExempt] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const submittingRef = useRef(false);
  const exemptRef = useRef(false);

  // HR can flag a staff member as exempt from face verification (their device
  // cannot run browser face detection) — those users check in on GPS alone.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/face/my-exemption`, { headers: h(), credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setFaceExempt(!!data.face_exempt);
          exemptRef.current = !!data.face_exempt;
        }
      } catch {}
    })();
  }, []);

  const alreadyCheckedIn = !!(todayRecord?.check_in);
  const alreadyCheckedOut = !!(todayRecord?.check_out);

  // Warm up a GPS reading on mount so the readiness chips show real status
  // (In Range / accuracy) before the user taps Check In.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation((prev) => prev || { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const requestLocation = (selectedAction) => {
    setGeoError(null);
    setGeoStatus('Getting your location...');
    setStep('geo');
    if (!navigator.geolocation) {
      setGeoError({ code: 'unsupported', message: 'Your browser does not support GPS / geolocation.' });
      return;
    }
    // iOS Safari often returns a poor first fix (100m+ accuracy). Watch the position
    // for up to 8s and keep the BEST reading; resolve early once accuracy <= 25m.
    let best = null;
    let settled = false;
    const finish = (pos) => {
      if (settled) return;
      settled = true;
      try { navigator.geolocation.clearWatch(watchId); } catch {}
      clearTimeout(timerId);
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
      setLocation(loc);
      setGeoStatus(`Location acquired (±${Math.round(pos.coords.accuracy)}m)`);
      setGeoError(null);
      if (exemptRef.current) {
        setGeoStatus('Marking attendance with GPS only...');
        submitAttendance(null, loc, selectedAction);
      } else {
        setStep('face');
      }
    };
    const fail = (err) => {
      if (settled) return;
      if (best) { finish(best); return; }  // use best reading even on late errors
      settled = true;
      try { navigator.geolocation.clearWatch(watchId); } catch {}
      clearTimeout(timerId);
      // err.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
      let msg = 'Could not get your location. Please try again.';
      if (err.code === 1) msg = 'Location access denied. Please allow location permission for this site and retry.';
      else if (err.code === 2) msg = 'Location is currently unavailable. Make sure GPS / location services are turned ON in your device settings.';
      else if (err.code === 3) msg = 'Location request timed out. Please move to an open area and retry.';
      setGeoError({ code: err.code, message: msg });
      setGeoStatus(msg);
    };
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
        setGeoStatus(`Improving GPS fix... (±${Math.round(best.coords.accuracy)}m)`);
        if (pos.coords.accuracy <= 25) finish(pos);
      },
      fail,
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
    const timerId = setTimeout(() => {
      if (best) finish(best);
      else fail({ code: 3 });
    }, 8000);
  };

  const startGeo = (selectedAction) => {
    // Guard: do not allow duplicate check-in / check-out for the same day
    if (selectedAction === 'check_in' && alreadyCheckedIn) {
      toast.info('You have already checked in today.');
      return;
    }
    if (selectedAction === 'check_out' && alreadyCheckedOut) {
      toast.info('You have already checked out today.');
      return;
    }
    if (selectedAction === 'check_out' && !alreadyCheckedIn) {
      toast.error('Please check in first before checking out.');
      return;
    }
    setAction(selectedAction);
    requestLocation(selectedAction);
  };

  const retryLocation = () => {
    if (action) requestLocation(action);
  };

  // Shown whenever a submit fails — including for GPS-only (exempt) staff, who never
  // reach the camera step. Never leave the user on a spinner with no way forward.
  const errorPanel = submitError ? (
    <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-3 text-xs space-y-2" data-testid="submit-error-panel">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-red-800">Could not save your attendance</p>
          <p className="text-red-700">{submitError}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 text-red-700 border-red-300 hover:bg-red-100"
          onClick={() => { setSubmitError(null); verifySaved(action || 'check_in'); }}
          data-testid="submit-error-check-btn">
          Check if it saved
        </Button>
        <Button size="sm" className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white"
          onClick={() => setSubmitError(null)} data-testid="submit-error-retry-btn">
          <RefreshCw className="w-3 h-3 mr-1.5" /> Try again
        </Button>
      </div>
    </div>
  ) : null;

  const handleFaceCapture = (descriptor, _snapshot) => submitAttendance(descriptor);

  // Called after a timeout / network drop: ask the server what today's record
  // actually looks like, so a slow-but-successful save is not reported as a failure.
  const verifySaved = async (act) => {
    try {
      const res = await fetch(`${API_URL}/api/attendance/my-status`, { headers: h(), credentials: 'include' });
      if (!res.ok) return false;
      const st = await res.json();
      const rec = st?.today_record || st?.today || st?.record || null;
      const time = act === 'check_in' ? rec?.check_in : rec?.check_out;
      if (!time) return false;
      const now = new Date();
      setDoneInfo({
        action: act, time,
        date: now.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }),
        work_hours: rec?.work_hours, late_mark: rec?.late_mark,
        requires_approval: rec?.requires_approval,
        message: 'Recorded successfully (the confirmation was delayed by a slow connection)',
      });
      setSubmitError(null);
      setFaceMismatchCount(0);
      setStep('done');
      if (onSuccess) onSuccess(st);
      toast.success('Your attendance was recorded — the confirmation was just slow to arrive.');
      return true;
    } catch {
      return false;
    }
  };

  const submitAttendance = async (descriptor, locOverride, actionOverride) => {
    const loc = locOverride || location;
    const act = actionOverride || action;
    if (!loc) return;
    // Hard guard against duplicate API submissions (rapid auto-trigger from face detection loop)
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setSubmitError(null);
    setLoading(true);
    try {
      const res = await postWithTimeout(`${API_URL}/api/attendance/face-geo-checkin`,
        { descriptor: descriptor || null, latitude: loc.lat, longitude: loc.lng, accuracy: loc.accuracy, action: act });
      const data = await safeJson(res);
      if (!res.ok) {
        // Track face mismatch failures so we can offer a Re-register path after repeated failures
        const detail = data.detail || 'Attendance marking failed. Please try again.';
        if (typeof detail === 'string' && detail.toLowerCase().includes('face does not match')) {
          setFaceMismatchCount((c) => c + 1);
        }
        throw new Error(detail);
      }
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
      const timeStr = data.action === 'check_in' ? data.record?.check_in : data.record?.check_out;
      // Prefer the backend's pre-composed, rich human message (includes minutes_late /
      // minutes_early / half-day / pending approval / distance from campus)
      const msg = data.message
        ? `Attendance marked — ${data.message}`
        : (data.action === 'check_in'
            ? `Attendance marked — Checked in on ${dateStr} at ${timeStr}${data.late_mark ? ' (Late)' : ''}`
            : `Attendance marked — Checked out on ${dateStr} at ${timeStr} (${(data.work_hours || 0).toFixed(1)}h worked)`);

      // Use a stronger toast variant when the user is LATE so it's not missed
      if (data.action === 'check_in' && (data.late_mark || data.minutes_late > 0)) {
        toast.warning(msg, { duration: 6000 });
      } else if (data.minutes_early > 0 && data.action === 'check_in') {
        toast.success(msg, { duration: 5000 });
      } else {
        toast.success(msg);
      }
      if (data.half_day) {
        toast.info(
          data.half_day === 'morning_half'
            ? 'This is recorded as a MORNING HALF-DAY. Please confirm with HR.'
            : 'This is recorded as an EVENING HALF-DAY. Please confirm with HR.'
        );
      }
      if (data.requires_approval) {
        toast.info('Note: This attendance requires HR approval as it is outside the allowed time window.');
      }
      setDoneInfo({
        action: data.action,
        time: timeStr,
        date: dateStr,
        work_hours: data.work_hours,
        late_mark: data.late_mark,
        requires_approval: data.requires_approval,
        minutes_late: data.minutes_late || 0,
        minutes_early: data.minutes_early || 0,
        half_day: data.half_day,
        distance: data.distance,
        message: data.message,
      });
      setFaceMismatchCount(0);
      setStep('done');
      if (onSuccess) onSuccess(data);
    } catch (e) {
      // A timeout does NOT mean the attendance failed — the write may have gone
      // through while the response was lost. Confirm against the server instead of
      // leaving the user staring at a spinner (or letting them double-submit,
      // which would be read as a check-out).
      if (e.timedOut || e.networkError) {
        const saved = await verifySaved(act);
        if (saved) {
          submittingRef.current = false;
          setLoading(false);
          return;
        }
      }
      setSubmitError(e.message);
      toast.error(e.message);
      if (exemptRef.current) setStep('select');
    }
    finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  // If both check-in and check-out are already done, hide the whole component (show "day complete" message)
  if (alreadyCheckedIn && alreadyCheckedOut && step === 'select') {
    return (
      <div className="flex flex-col items-center gap-2 py-4 px-3 rounded-lg bg-green-50 border border-green-200" data-testid="face-geo-day-complete">
        <CheckCircle className="w-7 h-7 text-green-600" />
        <p className="text-sm font-semibold text-green-800">Attendance complete for today</p>
        <p className="text-xs text-green-600">In: {todayRecord.check_in} · Out: {todayRecord.check_out}</p>
      </div>
    );
  }

  if (step === 'select') {
    const checkInDisabled = alreadyCheckedIn;
    const checkOutDisabled = !alreadyCheckedIn || alreadyCheckedOut;
    return (
      <div className="space-y-3 py-4" data-testid="face-geo-step-select">
        {errorPanel}
        <AttendanceReadiness location={location} />
        {faceExempt && (
          <div className="flex items-center justify-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2 px-3" data-testid="face-exempt-notice">
            <ShieldCheck className="w-4 h-4" />
            Face verification is not required for you — attendance is marked with your GPS location.
          </div>
        )}
        <p className="text-center text-sm text-slate-600 font-medium mb-3">What would you like to do?</p>
        <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
          <button
            type="button"
            onClick={() => startGeo('check_in')}
            disabled={checkInDisabled}
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
              checkInDisabled
                ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                : 'border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400'
            }`}
            data-testid="face-action-checkin"
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${checkInDisabled ? 'bg-slate-300' : 'bg-blue-500'}`}>
              <Check className="w-6 h-6 text-white" />
            </div>
            <span className={`text-sm font-semibold ${checkInDisabled ? 'text-slate-500' : 'text-blue-700'}`}>
              {checkInDisabled ? `Checked in at ${todayRecord?.check_in || ''}` : 'Check In'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => startGeo('check_out')}
            disabled={checkOutDisabled}
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
              checkOutDisabled
                ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                : 'border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-400'
            }`}
            data-testid="face-action-checkout"
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${checkOutDisabled ? 'bg-slate-300' : 'bg-orange-500'}`}>
              <X className="w-6 h-6 text-white" />
            </div>
            <span className={`text-sm font-semibold ${checkOutDisabled ? 'text-slate-500' : 'text-orange-700'}`}>
              {alreadyCheckedOut ? `Checked out at ${todayRecord?.check_out || ''}` : !alreadyCheckedIn ? 'Check In first' : 'Check Out'}
            </span>
          </button>
        </div>
      </div>
    );
  }

  if (step === 'geo') {
    if (geoError) {
      return (
        <div className="flex flex-col items-center gap-3 py-6 px-4" data-testid="face-geo-step-geo-error">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <p className="text-base font-semibold text-red-700 text-center">Location Required</p>
          <p className="text-sm text-slate-600 text-center max-w-xs">{geoError.message}</p>
          {geoError.code === 1 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 max-w-sm">
              <p className="font-semibold mb-1">How to enable location:</p>
              <ol className="list-decimal pl-4 space-y-0.5">
                <li>Tap the <strong>lock / info icon</strong> next to the URL in your browser</li>
                <li>Set <strong>Location</strong> to <strong>Allow</strong></li>
                <li>Reload the page if needed, then tap <strong>Retry</strong> below</li>
                <li>On phone: also turn on GPS in your device's location settings</li>
              </ol>
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => { setStep('select'); setGeoError(null); }} data-testid="geo-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={retryLocation} data-testid="geo-retry-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Retry Location
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.location.reload()} data-testid="geo-reload-btn">
              Reload Page
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-6" data-testid="face-geo-step-geo">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <p className="text-sm text-slate-600">{geoStatus}</p>
        <Badge variant="outline" className="text-xs">{action === 'check_in' ? 'Checking In' : 'Checking Out'}</Badge>
      </div>
    );
  }

  if (step === 'done') {
    const isCheckIn = doneInfo?.action === 'check_in';
    return (
      <div className="flex flex-col items-center gap-3 py-6" data-testid="face-geo-step-done">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <p className="text-lg font-semibold text-green-800">Attendance Marked!</p>
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-slate-700">
            {isCheckIn ? 'Checked in' : 'Checked out'} on {doneInfo?.date}
          </p>
          <p className="text-sm text-slate-600">at <span className="font-mono font-semibold">{doneInfo?.time}</span></p>
          {!isCheckIn && doneInfo?.work_hours != null && (
            <p className="text-xs text-slate-500">Work hours: {(doneInfo.work_hours || 0).toFixed(1)}h</p>
          )}
          {doneInfo?.distance != null && (
            <p className="text-xs text-slate-500" data-testid="done-distance">Distance from campus: {doneInfo.distance}m</p>
          )}
          <div className="flex gap-1.5 justify-center flex-wrap mt-2">
            {doneInfo?.minutes_late > 0 && (
              <Badge className="bg-red-100 text-red-700" data-testid="done-late-badge">
                ⏰ Late by {doneInfo.minutes_late} min
              </Badge>
            )}
            {doneInfo?.minutes_early > 0 && (
              <Badge className="bg-blue-100 text-blue-700" data-testid="done-early-badge">
                🌅 Early by {doneInfo.minutes_early} min
              </Badge>
            )}
            {doneInfo?.half_day === 'morning_half' && (
              <Badge className="bg-orange-100 text-orange-700" data-testid="done-halfday-badge">Morning Half-Day</Badge>
            )}
            {doneInfo?.half_day === 'evening_half' && (
              <Badge className="bg-orange-100 text-orange-700" data-testid="done-halfday-badge">Evening Half-Day</Badge>
            )}
            {doneInfo?.late_mark && !doneInfo?.minutes_late && (
              <Badge className="bg-orange-100 text-orange-700">Late Arrival</Badge>
            )}
            {doneInfo?.requires_approval && (
              <Badge className="bg-amber-100 text-amber-700">Awaiting HR Approval</Badge>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {isCheckIn ? 'Check-in disabled until tomorrow. Use Check Out when leaving.' : 'Day complete. See you tomorrow!'}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="face-geo-step-face">
      <AttendanceReadiness location={location} />
      <div className="text-center mb-3">
        <Badge className={`${action === 'check_in' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'} mb-2`}>
          {action === 'check_in' ? 'Checking In' : 'Checking Out'}
        </Badge>
        <Badge className="bg-green-100 text-green-700 ml-2 mb-2">
          <Check className="w-3 h-3 mr-1" /> GPS OK ({Math.round(location?.accuracy || 0)}m)
        </Badge>
        <p className="text-sm text-slate-600 font-medium mt-1">Look at the camera — face will auto-verify</p>
      </div>

      {faceMismatchCount >= 2 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3 text-xs space-y-2" data-testid="face-mismatch-help">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Face not matching?</p>
              <p className="text-amber-700">If your appearance has changed (haircut, glasses, beard) or your registered photo is poor quality, please re-register your face from <strong>Settings → Face Registration</strong>. The new photo will need HR approval before you can use it for attendance.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" className="w-full text-amber-700 border-amber-300 hover:bg-amber-100"
            onClick={() => { window.location.href = '/settings'; }}
            data-testid="reregister-from-attendance-btn">
            <RefreshCw className="w-3 h-3 mr-1.5" /> Go to Settings to Re-register
          </Button>
        </div>
      )}

      {errorPanel}

      <FaceCapture onCapture={handleFaceCapture} mode="checkin" loading={loading} />
    </div>
  );
}
